import { RadioCardGroup } from '@insify/design-system/components';
import { textMdSemibold } from '@insify/design-system/typography';
import clsx from 'clsx';
import { useController } from 'react-hook-form';

import { useCoveragesForm } from '../../useCoveragesForm';

import * as styles from './CoverageInputSelect.module.css';

import type { RadioOption } from '@insify/design-system/components';
import type { MoneyField } from '@insify/global-money';

export type CoverageInputSelectProps<CoverageType extends string> = {
  name: CoverageType;
  className?: string;
  label?: string;
  options: Pick<RadioOption<MoneyField>, 'label' | 'value' | 'hint'>[];
  required?: boolean;
};

export const CoverageInputSelect = <CoverageType extends string>({
  name,
  required,
  options = [],
  label,
  className,
  ...props
}: CoverageInputSelectProps<CoverageType>) => {
  const {
    coveragesFormMethods: { control },
    onSelect,
  } = useCoveragesForm();
  const {
    field: { onChange: onChangeFromField, ...fieldProps },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      validate: {
        cents: (value?: MoneyField) => (required && !Number(value?.cents) ? requiredMessage : undefined),
      },
    },
    shouldUnregister: true,
  });

  const handleOnChange = (amount: MoneyField) => {
    onChangeFromField(amount);
    onSelect({ type: name, amount });
  };

  const requiredMessage = required && `${name} is required`;

  const uniqueOptions = options.map(({ value, label, hint }) => ({
    id: `${name}_${value.cents}`,
    value,
    label: <RadioCardGroup.Heading>{label}</RadioCardGroup.Heading>,
    hint,
  }));

  return (
    <div className={className} {...props}>
      {label && <h3 className={clsx(textMdSemibold, styles.inputLabel)}>{label}</h3>}
      <RadioCardGroup<MoneyField>
        className={styles.input}
        {...fieldProps}
        onChange={handleOnChange}
        error={error?.message}
        options={uniqueOptions}
      />
    </div>
  );
};

export const CoverageInputDeductible = (props: Omit<CoverageInputSelectProps<'deductible'>, 'name'>) => (
  <CoverageInputSelect<'deductible'> name='deductible' {...props} data-testid='deductible-options' />
);
